/**
 * The sentiment screen (platform design §17.10): the headline score and its
 * day-by-day stance at one scope, then the cards that read the campaign's
 * terms, topics, localities, tag groups and worst posts for the same window.
 * Each card reads one route and none computes a figure the route did not
 * return.
 */

import type { ReactNode } from 'react';

import { Page, PageHeader } from '../../../chrome/page';
import { useOverview, usePosts, useSentiment } from '../api/queries';
import type { TerritorySelection } from '../api/territorySelection';
import { useWindowSelection, WindowSelect } from '../shared/WindowSelect';
import { DailyStanceCard } from './DailyStanceCard';
import { st2 } from './messages';
import { PostScoresCard } from './PostScoresCard';
import { RegionCard } from './RegionCard';
import { ScopePicker } from './ScopePicker';
import { SCOPE_KINDS, scopeRefOf, type ScopeKind } from './scopes';
import { SeriesSection } from './SeriesSection';
import { TagsCard } from './TagsCard';
import { TermsCard } from './TermsCard';
import { TopicCard } from './TopicCard';

const LISTED_POSTS = 50;

export function SentimentPage(
  props: { campaignId: string; tenantId: string } & TerritorySelection,
): ReactNode {
  const { campaignId, tenantId } = props;
  const {
    key: windowKey,
    window,
    onChange: setWindowKey,
    values,
    set,
  } = useWindowSelection(`sentiment.${campaignId}`, {
    scope: { default: 'campaign', values: SCOPE_KINDS },
    ref: { default: '' },
    dimension: { default: '' },
  });
  const kind = values.scope as ScopeKind;
  const ref = values.ref;

  // The posts route lists one tag at a time and the picker only needs some
  // posts to offer, so it reads the campaign's first tag.
  const tags = useOverview(campaignId, tenantId, { window });
  const posts = usePosts(campaignId, tenantId, {
    tag: tags.data?.by_tag[0]?.tag ?? '',
    window,
    limit: LISTED_POSTS,
  });
  const scopeRef = scopeRefOf(kind, ref, campaignId);
  const sentiment = useSentiment(campaignId, tenantId, { scopeRef, window });
  const postId = kind === 'post' ? ref : '';
  const reads = { campaignId, tenantId, window };

  return (
    <Page>
      <PageHeader
        title={st2('title')}
        actions={
          <>
            <WindowSelect value={windowKey} onChange={setWindowKey} />
            <ScopePicker
              kind={kind}
              onKindChange={(next) => set({ scope: next, ref: '' })}
              scopeRef={ref}
              onScopeRefChange={(next) => set({ ref: next })}
              posts={posts.data?.rows ?? []}
            />
          </>
        }
      />
      {/* Cards with nothing to say render nothing, so every card is a direct grid item and
          the last one of an odd count takes the full row instead of leaving a hole. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:[&>:last-child:nth-child(odd)]:col-span-2">
        <SeriesSection query={sentiment} window={window} />
        <DailyStanceCard {...reads} scopeRef={scopeRef} />
        <TermsCard {...reads} postId={postId} />
        <TopicCard {...reads} kind="against" />
        <TopicCard {...reads} kind="by" />
        <RegionCard {...reads} />
        <TagsCard
          {...reads}
          dimensionId={values.dimension}
          onDimensionChange={(next) => set({ dimension: next })}
        />
        <PostScoresCard {...reads} />
      </div>
    </Page>
  );
}
